import { createContext, useCallback, useContext, useMemo, useState } from 'react'

/**
 * Cross-component coordination for the review panel + overlay:
 *
 * - `focused` is the uuid of the comment thread / suggestion the panel or
 *   overlay just selected. Both surfaces listen for changes so clicking a
 *   pin scrolls the panel item into view, and clicking a panel item
 *   highlights the corresponding overlay pin.
 * - `requestPageJump` is bumped each time a focus change should trigger
 *   a scroll-to-page in the viewer. The detail page subscribes via
 *   `useReviewPageJump()` so it can call `scrollToPage()` on the
 *   `DocumentViewer` imperatively.
 *
 * Lives outside the queries layer because page-jump intent isn't server
 * state — it's a transient UI command that only exists between two React
 * components inside the same page render.
 */
export interface ReviewFocus {
  kind: 'comment' | 'suggestion'
  uuid: string
  page: number
}

interface ReviewFocusContextValue {
  focused: ReviewFocus | null
  /** Set focus + bump the page-jump counter. */
  focus: (f: ReviewFocus | null) => void
  /** Increments every time `focus` is called with a non-null value. */
  pageJumpToken: number
  /** Last requested page-jump target (1-based). */
  pageJumpTarget: number | null
  /**
   * 1-based page index reported by the viewer as the user scrolls. This is
   * the value the review composer anchors new comments to — `pageJumpTarget`
   * only updates on pin/jump clicks, so manual scrolling would otherwise
   * leave the composer anchored to a stale page.
   */
  currentViewerPage: number
  /** Setter for `currentViewerPage`. Clamps to a minimum of 1. */
  setCurrentViewerPage: (page: number) => void
}

const Ctx = createContext<ReviewFocusContextValue | null>(null)

export function ReviewFocusProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [focused, setFocused] = useState<ReviewFocus | null>(null)
  const [pageJumpToken, setToken] = useState(0)
  const [pageJumpTarget, setTarget] = useState<number | null>(null)
  const [currentViewerPage, setCurrentViewerPageRaw] = useState(1)

  const focus = useCallback((f: ReviewFocus | null) => {
    setFocused(f)
    if (f) {
      setTarget(f.page)
      setToken((n) => n + 1)
    }
  }, [])

  const setCurrentViewerPage = useCallback((page: number) => {
    setCurrentViewerPageRaw(Math.max(1, Math.floor(page)))
  }, [])

  const value = useMemo<ReviewFocusContextValue>(
    () => ({
      focused,
      focus,
      pageJumpToken,
      pageJumpTarget,
      currentViewerPage,
      setCurrentViewerPage,
    }),
    [
      focused,
      focus,
      pageJumpToken,
      pageJumpTarget,
      currentViewerPage,
      setCurrentViewerPage,
    ],
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useReviewFocus(): ReviewFocusContextValue {
  const v = useContext(Ctx)
  if (!v) {
    // Allow standalone usage in unit tests (provider-less). A no-op fallback
    // keeps the overlay/panel renderable without forcing test setup to wrap
    // everything in the provider.
    return {
      focused: null,
      focus: () => {},
      pageJumpToken: 0,
      pageJumpTarget: null,
      currentViewerPage: 1,
      setCurrentViewerPage: () => {},
    }
  }
  return v
}
